import { Hub } from '@aws-amplify/core';

import { HUB_CHANNEL as API_HUB_CHANNEL } from '../api/constants';
import { log } from '../logger';

import Postmate from 'postmate';
import { Account, StripeSubscriptionStatus } from '../types/Account';
import { LogColor } from '../utils/log';
import {
  EVENT_LOGIN_SUCCESS,
  HUB_CHANNEL as EXTENSION_HUB_CHANNEL,
} from './constants';

const COLOR: LogColor = 'pink';

/** stripe subscription status (DUPED in extension UserTypes.ts) */
export type LoginSubStatus = StripeSubscriptionStatus | '';

/**
 * Login data that we pass to the extension, so
 * it knows the user's state.
 *
 * Example from the extension:
 *
 * ```json
 * {
 *   expires: 1769834032,
 *   status: "active",
 *   updated: 1738298673,
 *   userId: "us-east-1:2d13ada2-f05f-c3b5-c9ab-46faded4d247"
 * }
 * ```
 *
 * NOTE: I think updated is added inside the extension?
 */
type LoginData = {
  userId: string;
  orgId: string;
  expires: number;
  status: LoginSubStatus;
  show_experimental?: boolean;
  sub_created?: number;
};

const loginUser = ({
  postmateChild,
  userId,
  orgId,
  subCreatedUnixTimestamp,
  expiresUnixTimestamp,
  status,
  // TODO //NEXTTTTTTT - update this web code to pass through info for:
  // 1. how long into subscription, and... (`createdUnixTimestamp`)
  // 2. whether enterprise or not (`account_type` or `orgId`?)
  //
  // then..
  // 1. update extension to handle reading this info
  //    to determine showing the extension modal
  // 2. also update extension to jspdf 3.0.0 and remove the dompurify dep
  //
}: {
  postmateChild: Postmate.ParentAPI;
  userId: string;
  orgId?: string;
  subCreatedUnixTimestamp: number;
  expiresUnixTimestamp: number;
  status: LoginSubStatus;
  showExperimental?: boolean;
}) => {
  // data needs:
  // * token
  // * expires
  // * status

  // TODO - is this partially duplicating work from ExtensionAPIServer.js -> authState call?

  const data: LoginData = {
    userId,
    orgId: orgId || '',
    expires: expiresUnixTimestamp,
    status: status || '',
    sub_created: subCreatedUnixTimestamp,
  };

  // HACK - add show_experimental
  const tokenKey = 'showExperimental';
  const token = window.location.search
    .substring(1)
    .split('&')
    .filter((x) => x === tokenKey || x.startsWith(tokenKey + '='))[0];
  if (token) {
    const sp = token.split('=');
    const tokenVal = sp[1];
    const show_experimental =
      !tokenVal ||
      ['t', '1', 'y'].indexOf(tokenVal.substring(0, 1).toLowerCase()) !== -1;
    data.show_experimental = show_experimental;
  }

  // there is no callback or Promise for Postmate.call
  postmateChild.call('login', data);
};

export default class LoginListener {
  postmateProm: Promise<Postmate.ParentAPI>;

  constructor(postmateProm: Promise<Postmate.ParentAPI>) {
    Hub.listen(API_HUB_CHANNEL, this.onHubCapsule, 'LoginListener');
    this.postmateProm = postmateProm;
  }

  destroy() {
    Hub.remove(API_HUB_CHANNEL, this.onHubCapsule);
  }

  // hub listener
  onHubCapsule = ({
    channel,
    payload,
    source,
  }: {
    channel: string;
    payload: unknown;
    source: unknown;
  }) => {
    //
    if (channel === API_HUB_CHANNEL) {
      // HACK - fake the types :(
      const _payload = payload as {
        data?: {
          pathPrefix?: string;
          object?: Account;
        };
      };

      const object = _payload.data?.object;
      if (object && _payload.data?.pathPrefix === 'account') {
        log.x(
          '[LoginListener.onHubCapsule]',
          { channel, payload, source, object },
          COLOR,
        );

        const sub = object.subscription;
        const userId = object.userId;
        const subCreatedUnixTimestamp = sub?.created?.unix() || 0;
        const expiresUnixTimestamp = sub?.current_period_end?.unix() || 0;
        const status = sub ? sub.status : '';
        const orgId = object.orgId;

        this.postmateProm
          .then((postmateChild) => {
            // setup login-success handler - 'login-success' HARDCODED in extension
            // TODO - "once" this, so it doesn't pile up and execute a bunch of times
            postmateChild.on('login-success', (data) => {
              log.x(
                '[LoginListener.onHubCapsule.postmateProm.login-success]',
                { data },
                COLOR,
              );
              // status string is from Stripe - using "subOk" logic from extension
              if (data.status && data.status !== 'canceled') {
                Hub.dispatch(
                  EXTENSION_HUB_CHANNEL,
                  { event: EVENT_LOGIN_SUCCESS, data },
                  'LoginListener',
                );
              }
            });

            // execute the remote login call to the extension
            log.x(
              '{{update?}}',
              { userId, subCreatedUnixTimestamp, expiresUnixTimestamp, status },
              COLOR,
            );
            loginUser({
              postmateChild,
              userId,
              orgId,
              subCreatedUnixTimestamp,
              expiresUnixTimestamp,
              status,
            });
          })
          .catch((err: unknown) => {
            log.error('[LoginListener.onHubCapsule.postmateProm.error]', err);
          });
      }
    }
  };
}
